import {
  DEFAULT_WORLD_BALANCE,
  type WorldBalance,
} from "../../models/config/worldBalance.ts";
import type { DomainEvent } from "../../models/events.ts";
import type {
  EventTileCoordinates,
  RandomEventEngineState,
} from "../../models/randomEvent.ts";
import type { RngState, WorldState } from "../../models/worldState.ts";
import { nextRngUnit } from "../../models/rng/seededRng.ts";
import { getDefaultRandomEventDefinitions } from "./eventDefinitions.ts";
import {
  forceActivateEvent,
  type RandomEventLifecycleNotification,
} from "./processTurnEnd.ts";

function createConsumingRandom(rng: RngState): {
  random: () => number;
  getRng: () => RngState;
} {
  let current = rng;
  return {
    random: () => {
      const next = nextRngUnit(current);
      current = next.rng;
      return next.value;
    },
    getRng: () => current,
  };
}

function lifecycleToDomainEvents(
  notifications: readonly RandomEventLifecycleNotification[],
): DomainEvent[] {
  const events: DomainEvent[] = [];
  for (const notification of notifications) {
    if (notification.type === "eventStarted") {
      events.push({
        type: "RandomEventStarted",
        eventId: notification.eventId,
        startedAtTurn: notification.startedAtTurn,
        tile: notification.tile,
      });
    } else if (notification.type === "eventTileEntered") {
      events.push({
        type: "RandomEventTileEntered",
        eventId: notification.eventId,
        tile: notification.tile,
      });
    } else {
      events.push({
        type: "RandomEventFinished",
        eventId: notification.eventId,
        reason: notification.reason,
      });
    }
  }
  return events;
}

/** Forced activation for map timeline `startEvent` beats (bypasses probability). */
export function applyForcedRandomEvent(
  state: WorldState,
  eventId: string,
  activationTurn: number,
  tile?: EventTileCoordinates,
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): {
  randomEvent: RandomEventEngineState;
  rng: RngState;
  events: DomainEvent[];
} {
  const definition = getDefaultRandomEventDefinitions(balance).find(
    (entry) => entry.id === eventId,
  );
  if (!definition) {
    return {
      randomEvent: state.randomEvent,
      rng: state.rng,
      events: [],
    };
  }

  const { random, getRng } = createConsumingRandom(state.rng);
  const result = forceActivateEvent(
    state.randomEvent,
    definition,
    {
      currentTurn: activationTurn,
      clanTile: tile ?? state.clan.currentTile,
    },
    tile,
    random,
  );

  return {
    randomEvent: result.state,
    rng: getRng(),
    events: lifecycleToDomainEvents(result.notifications),
  };
}
